import { useMemo } from 'react';

import { exportPlotlyWidgetToPng } from '../../../../report/widgetExportHelpers';
import type { WidgetInstanceCapabilities } from '../../../../runtime/instanceRegistry/types';
import { useRegisterWidgetInstance } from '../../../../runtime/instanceRegistry/useRegisterWidgetInstance';

// -- type definitions --

export interface PlotPngInstanceRegistrationContext {
  readonly dashboardElementId: number;
  readonly widgetId: string;
  readonly plotName: string;
  readonly enabled: boolean;
}

// -- hook --

/**
 * Register a plot widget instance.
 *
 * The following capabilities are registered:
 * - for-report content extraction: PNG format
 */
export function usePlotPngInstanceRegistration(
  ctx: PlotPngInstanceRegistrationContext
): void {
  const capabilities = useMemo<WidgetInstanceCapabilities | null>(() => {
    if (!ctx.enabled) {
      return null;
    }

    return {
      dashboardElementId: ctx.dashboardElementId,
      widgetId: ctx.widgetId,
      toolbarActions: [
        {
          id: 'download-png',
          buttonIcon: 'download',
          tooltipText: 'Télécharger en PNG',
          order: 10,
          performAction: async () => {
            const dataUrl = await exportPlotlyWidgetToPng(ctx.widgetId);
            const anchor = document.createElement('a');
            anchor.href = dataUrl;
            anchor.download = `${ctx.plotName}.png`;
            anchor.click();
          },
        },
      ],
      reportExports: [
        {
          format: 'png',
          export: async () => exportPlotlyWidgetToPng(ctx.widgetId),
        },
      ],
    };
  }, [ctx.dashboardElementId, ctx.enabled, ctx.plotName, ctx.widgetId]);

  useRegisterWidgetInstance(capabilities);
}
